import { Box, Typography, CircularProgress, Select, MenuItem } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { UserActionLogItem } from 'services/auditApi';
import { METHOD_STYLES, formatTimestamp } from './helpers';

const COLS = '170px 1.4fr 110px 1.3fr 84px 1.5fr 36px';
const MIN_WIDTH = 960;
const PAGE_SIZES = [25, 50, 100];

const HEADERS = ['Время', 'Пользователь', 'Категория', 'Действие', 'Метод', 'URL', ''];

interface LogTableProps {
  items: UserActionLogItem[];
  count: number;
  loading: boolean;
  limit: number;
  offset: number;
  onPage: (offset: number) => void;
  onLimit: (limit: number) => void;
  onRowClick: (item: UserActionLogItem) => void;
}

function Cell({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <Typography
      sx={{
        fontSize: 12.5,
        color: muted ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </Typography>
  );
}

export default function LogTable({
  items,
  count,
  loading,
  limit,
  offset,
  onPage,
  onLimit,
  onRowClick,
}: LogTableProps) {
  const from = count === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, count);
  const canPrev = offset > 0;
  const canNext = offset + limit < count;

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        border: '1px solid var(--color-border)',
        boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      {/* Header label */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Журнал действий</Typography>
        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
          {count} записей
        </Typography>
      </Box>

      {/* Horizontal scroll wrapper для широкой таблицы */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: MIN_WIDTH }}>
          {/* Column headers */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: COLS,
              gap: 1.5,
              px: 2.5,
              py: 1,
              bgcolor: 'var(--color-surface-subtle)',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            {HEADERS.map((h, i) => (
              <Typography
                key={h || `col-${i}`}
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--color-text-muted)',
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {/* Loading */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={24} sx={{ color: 'var(--color-primary)' }} />
            </Box>
          )}

          {/* Empty */}
          {!loading && items.length === 0 && (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                Записей не найдено
              </Typography>
            </Box>
          )}

          {/* Rows */}
          {!loading &&
            items.map((item, i) => {
              const method = METHOD_STYLES[item.http_method];
              return (
                <Box
                  key={item.id}
                  onClick={() => onRowClick(item)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: COLS,
                    gap: 1.5,
                    px: 2.5,
                    py: 1.5,
                    alignItems: 'center',
                    borderBottom: i < items.length - 1 ? '1px solid var(--color-border)' : 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'var(--color-surface-subtle)' },
                  }}
                >
                  <Cell muted>{formatTimestamp(item.created_at)}</Cell>
                  <Cell>{item.user_email ?? '—'}</Cell>
                  <Box sx={{ display: 'inline-flex', minWidth: 0 }}>
                    <Box
                      sx={{
                        bgcolor: 'var(--color-primary-tint)',
                        color: 'var(--color-primary)',
                        borderRadius: '6px',
                        px: 1,
                        py: '3px',
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {item.category}
                    </Box>
                  </Box>
                  <Cell>{item.action}</Cell>
                  <Box sx={{ display: 'inline-flex' }}>
                    <Box
                      sx={{
                        bgcolor: method?.bg ?? 'var(--color-surface-subtle)',
                        color: method?.color ?? 'var(--color-text-secondary)',
                        borderRadius: '6px',
                        px: 1,
                        py: '2px',
                        fontSize: 10.5,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.http_method}
                    </Box>
                  </Box>
                  <Cell muted>
                    <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 11.5 }}>
                      {item.url}
                    </Box>
                  </Cell>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    <ChevronRight size={15} />
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>

      {/* Pagination footer */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            Строк на странице
          </Typography>
          <Select
            value={limit}
            onChange={(e) => onLimit(Number(e.target.value))}
            size="small"
            sx={{
              fontSize: 12.5,
              '& .MuiSelect-select': { py: '4px', pr: '24px !important', pl: '8px' },
              '& fieldset': { borderColor: 'var(--color-border)' },
            }}
          >
            {PAGE_SIZES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: 12.5 }}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-secondary)' }}>
          {from}–{to} из {count}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <PageBtn disabled={!canPrev} onClick={() => onPage(Math.max(0, offset - limit))}>
            <ChevronLeft size={16} />
          </PageBtn>
          <PageBtn disabled={!canNext} onClick={() => onPage(offset + limit)}>
            <ChevronRight size={16} />
          </PageBtn>
        </Box>
      </Box>
    </Box>
  );
}

function PageBtn({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        width: 30,
        height: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--color-border)',
        color: disabled ? 'var(--color-text-placeholder)' : 'var(--color-text-secondary)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
        '&:hover': disabled
          ? undefined
          : {
              bgcolor: 'var(--color-primary-tint)',
              borderColor: 'var(--color-primary)',
              color: 'var(--color-primary)',
            },
      }}
    >
      {children}
    </Box>
  );
}
